import { UserFileTag } from '__generated__/api-types-and-hooks'
import { IUserFileLink } from 'components/Common/AppFilesCard/useOpenUserFile'

/** A getUserFiles record. Chat-shared ones may also say who shared them and where. */
export interface ILibraryFile extends IUserFileLink {
  size?: string | number | null
  type?: string | null
  createdAt?: string | null
  tag?: UserFileTag | null
  source?: string | null
  uploadedBy?: string | null
  uploadedByName?: string | null
  channelId?: string | null
}

export type FileCategory = 'documents' | 'spreadsheets' | 'images' | 'other'
export type FileFilter = 'all' | 'chat' | 'uploaded'

export const FILE_CATEGORIES: FileCategory[] = ['documents', 'spreadsheets', 'images', 'other']

const EXTENSIONS: Record<Exclude<FileCategory, 'other'>, string[]> = {
  spreadsheets: ['xls', 'xlsx', 'xlsm', 'ods', 'csv', 'tsv', 'numbers'],
  documents: [
    'pdf',
    'doc',
    'docx',
    'odt',
    'rtf',
    'txt',
    'md',
    'pages',
    'ppt',
    'pptx',
    'pps',
    'ppsx',
    'odp',
    'key',
  ],
  images: [
    'png',
    'jpg',
    'jpeg',
    'gif',
    'svg',
    'webp',
    'heic',
    'heif',
    'bmp',
    'tif',
    'tiff',
    'avif',
  ],
}

export const getExtension = (name?: string | null) => {
  const match = /\.([^./\\]+)$/.exec(name || '')
  return match ? match[1].toLowerCase() : ''
}

export const getFileCategory = (file: ILibraryFile): FileCategory => {
  const ext = getExtension(file.name)
  if (EXTENSIONS.spreadsheets.includes(ext)) return 'spreadsheets'
  if (EXTENSIONS.documents.includes(ext)) return 'documents'
  if (EXTENSIONS.images.includes(ext)) return 'images'
  const type = (file.type || '').toLowerCase()
  if (type.startsWith('image/')) return 'images'
  if (/spreadsheet|excel|csv/.test(type)) return 'spreadsheets'
  if (/pdf|word|document|presentation|powerpoint|rtf|text\/plain/.test(type)) return 'documents'
  return 'other'
}

/** Older chat records only carry the Stream message id; newer ones say source: 'chat'. */
export const isChatFile = (file: ILibraryFile) => file.source === 'chat' || !!file.streamMessageId

export const isKeyFile = (file: ILibraryFile) =>
  file.tag === UserFileTag.BusinessPlan || file.tag === UserFileTag.Logo

export const formatFileSize = (size?: string | number | null) => {
  const bytes = typeof size === 'string' ? Number(size) : size
  if (bytes === null || bytes === undefined || !isFinite(bytes) || bytes < 0) return ''
  if (bytes < 1000) return `${bytes} B`
  if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} KB`
  if (bytes < 1_000_000_000) return `${+(bytes / 1_000_000).toFixed(1)} MB`
  return `${+(bytes / 1_000_000_000).toFixed(1)} GB`
}

// The end of a long name that always shows: its last word (at least TAIL_MIN characters, so
// "Screen Shot … at 10.15.42.png" and "… at 10.16.03.png" can be told apart) but no more than
// TAIL_MAX, then the extension
const TAIL_MIN = 6
const TAIL_MAX = 12
const WORD_BREAK = /[\s_-]/
// A shortened name never shows its end starting with one of these
const LEADING_PUNCTUATION = /[\s.,;:!?_\-\u2013\u2014\u00b7]/

/**
 * Splits a name into a head that may shorten and a tail that always shows, breaking between
 * words: "Screen Shot 2026-09-28 at 10.15.42.png" → ["Screen Shot 2026-09-28 at ", "10.15.42.png"],
 * "IMG_20260921_143512.jpg" → ["IMG_20260921_", "143512.jpg"].
 * Counts characters, not UTF-16 units, so an emoji is never cut in half.
 */
export const splitFileName = (name: string): [string, string] => {
  const chars = Array.from(name)
  const ext = getExtension(name)
  // Where the extension starts (the end of the name when it has none)
  const stem = ext && chars.length > ext.length + 1 ? chars.length - ext.length - 1 : chars.length
  let start = stem
  for (let i = stem - 1; i >= Math.max(0, stem - TAIL_MAX - 1); i--) {
    const next = i + 1
    if (WORD_BREAK.test(chars[i]) && next < stem && !LEADING_PUNCTUATION.test(chars[next])) {
      start = next
      if (stem - start >= TAIL_MIN) break
    }
  }
  // One long word: keep its last few characters
  if (start === stem) {
    start = Math.max(0, stem - TAIL_MIN)
    while (start < stem && LEADING_PUNCTUATION.test(chars[start])) start++
  }
  // Short names never need shortening in the middle
  if (start < TAIL_MIN) return [name, '']
  return [chars.slice(0, start).join(''), chars.slice(start).join('')]
}

const INTL_LOCALES: Record<string, string> = { tl: 'fil', zh: 'zh-CN' }

/** "Feb 15" this year, "Feb 15, 2025" before, in the UI language ("15 thg 2, 2025" in vi). */
export const formatFileDate = (
  date: string | null | undefined,
  lang?: string | null,
  { withYear = false, now = new Date() } = {}
) => {
  const value = Date.parse(date || '')
  if (isNaN(value)) return ''
  const day = new Date(value)
  const base = (lang || 'en').toLowerCase().split('-')[0]
  const showYear = withYear || day.getFullYear() !== now.getFullYear()
  const options: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short' }
  if (showYear) options.year = 'numeric'
  try {
    return new Intl.DateTimeFormat(INTL_LOCALES[base] || base, options).format(day)
  } catch {
    return day.toLocaleDateString()
  }
}

const time = (file: ILibraryFile) => {
  const value = Date.parse(file.createdAt || '')
  return isNaN(value) ? 0 : value
}

export const newestFirst = (a: ILibraryFile, b: ILibraryFile) => time(b) - time(a)

/** The name contains what was typed (ignoring case and surrounding spaces). */
export const matchesQuery = (file: ILibraryFile, query = '') => {
  const q = query.trim().toLowerCase()
  return !q || (file.name || '').toLowerCase().includes(q)
}

export const countBySource = (files: ILibraryFile[]) => {
  const chat = files.filter(isChatFile).length
  return { all: files.length, chat, uploaded: files.length - chat }
}

export interface IFileGroups {
  key: ILibraryFile[]
  sections: { id: FileCategory; files: ILibraryFile[] }[]
  total: number
}

/** Filters by source and name, then groups: key documents first, the rest by kind, newest first. */
export const groupFiles = (
  files: ILibraryFile[],
  filter: FileFilter = 'all',
  query = ''
): IFileGroups => {
  const matches = files.filter(
    (file) =>
      (filter === 'all' || (filter === 'chat') === isChatFile(file)) && matchesQuery(file, query)
  )
  const key = matches
    .filter(isKeyFile)
    .sort((a, b) =>
      a.tag === b.tag ? newestFirst(a, b) : a.tag === UserFileTag.BusinessPlan ? -1 : 1
    )
  const rest = matches.filter((file) => !isKeyFile(file))
  const sections = FILE_CATEGORIES.map((id) => ({
    id,
    files: rest.filter((file) => getFileCategory(file) === id).sort(newestFirst),
  })).filter((section) => section.files.length > 0)
  return { key, sections, total: matches.length }
}
